import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'lib/utils';

const buttonVariants = cva(
    // appearance-none: Tailwind preflight is off (MUI compat), so reset native <button> chrome.
    // border-none on filled variants / border-solid on outline: avoid UA 3D bevel while still
    // letting outline buttons show a real border on light backgrounds.
    'inline-flex appearance-none cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default:
                    'border-none bg-primary text-primary-foreground shadow hover:bg-primary/90',
                destructive:
                    'border-none bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
                outline:
                    'border border-solid border-border bg-card text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground',
                secondary:
                    'border-none bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
                ghost: 'border-none hover:bg-accent hover:text-accent-foreground',
                link: 'border-none text-primary underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-9 px-4 py-2',
                sm: 'h-8 rounded-md px-3 text-xs',
                lg: 'h-10 rounded-md px-8',
                icon: 'h-9 w-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(buttonVariants({ variant, size }), className)}
                ref={ref}
                {...props}
            />
        );
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
